"use client";

import React from "react";
import { ArrowRight, Truck, Compass, Wrench, ShieldAlert, Cpu } from "lucide-react";
import { motion, useReducedMotion } from "framer-motion";
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
interface CostCardItem {
  id: string;
  title: string;
  description: string;
  icon: React.ComponentType<{
    className?: string;
  }>;
  linkText?: string;
  linkTo?: string;
}
const COST_ITEMS: CostCardItem[] = [{
  id: "shipping",
  title: "Shipping",
  description: "Confirm the actual delivery arrangement for the machine and destination.",
  icon: Truck,
  linkText: "Shipping Information",
  linkTo: "/ShippingWarrantyAndOrderingPage"
}, {
  id: "site-prep",
  title: "Site Preparation",
  description: "Check whether your location already has suitable space, access and the required electrical supply for the exact equipment.",
  icon: Compass,
  linkText: "Setup & Safety",
  linkTo: "/SetupSafetyAndMaintenancePage"
}, {
  id: "replacement-parts",
  title: "Replacement Parts",
  description: "Consider the availability and cost of components that may eventually need replacement.",
  icon: Wrench,
  linkText: "Parts & Accessories",
  linkTo: "/MechanicalBullPartsAndAccessoriesPage"
}, {
  id: "inflatable-blower",
  title: "Inflatable or Blower Replacement",
  description: "For machines using an inflatable safety arena, consider the condition and future availability of the inflatable and blower.",
  icon: ShieldAlert
}, {
  id: "maintenance",
  title: "Maintenance",
  description: "Follow the manufacturer's or product-specific maintenance guidance where available rather than applying one generic maintenance schedule to every machine.",
  icon: Cpu
}];
export default function Costs() {
  const shouldReduceMotion = useReducedMotion();
  const containerVariants = {
    hidden: {},
    show: {
      transition: {
        staggerChildren: shouldReduceMotion ? 0 : 0.06
      }
    }
  };
  const itemVariants = {
    hidden: {
      opacity: 0,
      y: shouldReduceMotion ? 0 : 12
    },
    show: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.3,
        ease: [0.16, 1, 0.3, 1]
      }
    }
  };
  return <section id="costsbeyondmachineprice" data-nav="light" className="w-full border-y border-border bg-background py-16 md:py-24">
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <motion.div initial={{
        opacity: 0,
        y: shouldReduceMotion ? 0 : 12
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} transition={{
        duration: 0.3,
        ease: [0.16, 1, 0.3, 1]
      }} className="mb-12 max-w-3xl">
          <div className="mb-3 inline-flex items-center gap-2 font-mono text-xs font-semibold tracking-wider text-primary uppercase">
            <span className="inline-block size-2 bg-primary" />
            Ownership & Logistics Expenses
          </div>
          <h2 className="font-serif text-3xl font-bold tracking-tight text-foreground uppercase sm:text-4xl md:text-5xl">
            Costs to check beyond the listed machine price
          </h2>
          <p className="mt-4 text-base leading-relaxed text-muted-foreground sm:text-lg">
            The product price is the starting point, not always the entire ownership cost. Before purchasing, check which additional expenses apply to the exact machine and your location.
          </p>
        </motion.div>

        <motion.div variants={containerVariants} initial="hidden" whileInView="show" viewport={{
        once: true
      }} className="grid grid-cols-1 gap-6 md:grid-cols-2 lg:grid-cols-3">
          {COST_ITEMS.map((item, i) => {
          const IconComponent = item.icon;
          return <motion.div key={item.id} data-index={i} variants={itemVariants} className="group flex flex-col justify-between border border-border bg-card p-6 transition-[border-color,box-shadow] duration-200 hover:border-primary/60 hover:shadow-sm">
                <div>
                  <div className="mb-4 flex items-center justify-between">
                    <div className="flex size-10 items-center justify-center border border-border bg-muted text-foreground">
                      <IconComponent className="size-5 text-foreground" />
                    </div>
                    <span className="font-mono text-xs text-muted-foreground">
                      {"0"}
                      {i + 1}
                    </span>
                  </div>

                  <h3 className="font-serif text-xl font-bold tracking-tight text-foreground uppercase">
                    {item.title}
                  </h3>

                  <p className="mt-3 text-sm leading-relaxed text-muted-foreground">
                    {item.description}
                  </p>
                </div>

                {item.linkText && item.linkTo ? <div className="mt-6 border-t border-border pt-4">
                    <Button asChild variant="outline" size="sm" className="w-full justify-between font-mono text-xs font-semibold uppercase transition-[color,background-color,border-color] duration-150 hover:bg-muted">
                      <Link to={item.linkTo}>
                        <span>{item.linkText}</span>
                        <ArrowRight className="size-3.5 text-primary" />
                      </Link>
                    </Button>
                  </div> : <div className="mt-6 border-t border-border/50 pt-4">
                    <span className="font-mono text-xs tracking-wider text-muted-foreground uppercase">
                      Standard review item
                    </span>
                  </div>}
              </motion.div>;
        })}
        </motion.div>
      </div>
    </section>;
}